import { TFICDocument } from "../../../../views/DocumentDrawer/types/Document.ts";

export function CalculationsSection({ document }: { document: TFICDocument }) {
    if (!document.line_items || document.line_items.length === 0) return null;

    const docTotal = document.scanned_fields?.total_amount_payable?.signed_amount ?? null;
    const docVat = document.scanned_fields?.vat_amount_overall?.signed_amount ?? null;
    const docNet = docTotal - docVat;

    const { netSum, vatSum, totalSum } = document.line_items.reduce(
        (acc, item) => ({
            netSum: acc.netSum + (item.net_amount?.signed_amount ?? 0),
            vatSum: acc.vatSum + (item.vat_amount?.signed_amount ?? 0),
            totalSum: acc.totalSum + (item.total_amount?.signed_amount ?? 0),
        }),
        { netSum: 0, vatSum: 0, totalSum: 0 }
    );

    const wrongLineItems = document.line_items.filter((item) => {
        const net = item.net_amount?.signed_amount ?? 0;
        const vat = item.vat_amount?.signed_amount ?? 0;
        const total = item.total_amount?.signed_amount ?? 0;
        return net + vat !== total;
    });

    const netDiff = docNet != null && docNet !== netSum;
    const vatDiff = docVat != null && docVat !== vatSum;
    const totalDiff = docTotal != null && docTotal !== totalSum;

    return (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">Calculations</h3>
            <div className="grid grid-cols-2 gap-6">
                <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
                    <h4 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">Document</h4>
                    <dl className="space-y-2">
                        <div className="flex justify-between font-mono text-sm">
                            <span className="text-gray-500">net</span>
                            <span>{docNet ?? '-'}</span>
                        </div>
                        <div className="flex justify-between font-mono text-sm">
                            <span className="text-gray-500">vat</span>
                            <span>{docVat ?? '-'}</span>
                        </div>
                        <div className="flex justify-between font-mono text-sm font-medium">
                            <span className="text-gray-500">total</span>
                            <span>{docTotal ?? '-'}</span>
                        </div>
                    </dl>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
                    <h4 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">Line items totals</h4>
                    <dl className="space-y-2">
                        <div className="flex justify-between font-mono text-sm">
                            <span className="text-gray-500">net_sum</span>
                            <span className={netDiff ? 'font-medium text-red-500' : ''}>{netSum}</span>
                        </div>
                        <div className="flex justify-between font-mono text-sm">
                            <span className="text-gray-500">vat_sum</span>
                            <span className={vatDiff ? 'font-medium text-red-500' : ''}>{vatSum}</span>
                        </div>
                        <div className="flex justify-between font-mono text-sm font-medium">
                            <span className="text-gray-500">total_sum</span>
                            <span className={totalDiff ? 'font-medium text-red-500' : ''}>{totalSum}</span>
                        </div>
                    </dl>
                </div>
            </div>
            {wrongLineItems.length > 0 && (
                <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-1">{wrongLineItems.length} line items with net + vat ≠ total</h4>
                    <p className="text-xs text-gray-600 mb-3">Stored total differs from net + vat per line</p>
                    <div className="overflow-x-auto max-h-48 overflow-y-auto">
                        <table className="w-full text-xs font-mono">
                            <thead>
                                <tr className="text-left text-gray-500 border-b border-gray-200">
                                    <th className="py-1.5 pr-4">id</th>
                                    <th className="py-1.5 pr-4">net</th>
                                    <th className="py-1.5 pr-4">vat</th>
                                    <th className="py-1.5 pr-4">sum</th>
                                    <th className="py-1.5 pr-4">total</th>
                                    <th className="py-1.5 pr-4">diff</th>
                                </tr>
                            </thead>
                            <tbody>
                                {wrongLineItems.map((item) => {
                                    const net = item.net_amount?.signed_amount ?? 0;
                                    const vat = item.vat_amount?.signed_amount ?? 0;
                                    const total = item.total_amount?.signed_amount ?? 0;
                                    const diff = total - (net + vat);
                                    return (
                                        <tr key={item.id} className="border-b border-gray-100 last:border-0">
                                            <td className="py-1.5 pr-4 text-gray-500 truncate max-w-[120px]" title={item.id}>{item.id.slice(0, 8)}…</td>
                                            <td className="py-1.5 pr-4">{net}</td>
                                            <td className="py-1.5 pr-4">{vat}</td>
                                            <td className="py-1.5 pr-4">{net + vat}</td>
                                            <td className="py-1.5 pr-4">{total}</td>
                                            <td className="py-1.5 pr-4 font-medium">{diff}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
